/**
 * SellerPaymentsTab — per-store payment-method configuration for the
 * seller hub. Lets the seller flip COD / QR / Bank / Online Gateway,
 * fill in the per-method details (QR image, bank acct, etc.) and copy
 * one store's config to every other store they own.
 *
 * Data shape (from GET /api/seller/payments/:store_id):
 *   { store_id, owner_id, methods: { cod|qr|bank|gateway: {enabled, ...} },
 *     contact: { email, phone, pickup_address },
 *     policies: [{method, master_enabled, seller_deny[]}],
 *     catalog:  [{id, label, icon, fields[]}] }
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import {
  Save, Truck, QrCode, Landmark, CreditCard, Copy, ShieldX,
  Mail, Phone, MapPin, AlertCircle, Loader2, CheckCircle2,
} from "lucide-react";

const ICONS = {
  Truck, QrCode, Landmark, CreditCard,
};

export default function SellerPaymentsTab({ stores }) {
  const { user } = useAuth();
  const [storeId, setStoreId] = useState("");
  const [config, setConfig] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // Pick a default store
  useEffect(() => {
    if (!storeId && stores?.length) setStoreId(stores[0].store_id);
  }, [stores, storeId]);

  const load = async () => {
    if (!storeId) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get(`/seller/payments/${storeId}`);
      setConfig({
        methods: data.methods,
        contact: data.contact || { email: "", phone: "", pickup_address: "" },
      });
      setPolicies(data.policies || []);
      setCatalog(data.catalog || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [storeId]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 1800); };

  const setMethod = (id, patch) => {
    setConfig((c) => ({
      ...c,
      methods: { ...c.methods, [id]: { ...c.methods[id], ...patch } },
    }));
  };

  const setContact = (patch) => {
    setConfig((c) => ({ ...c, contact: { ...(c.contact || {}), ...patch } }));
  };

  const save = async () => {
    if (!config) return;
    setBusy(true); setErr(null);
    try {
      await api.put(`/seller/payments/${storeId}`, {
        methods: config.methods, contact: config.contact,
      });
      flash("Payment settings saved.");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const applyAll = async () => {
    if (!storeId) return;
    if (!confirm("Copy these payment settings to every other store you own?")) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/seller/payments/apply-all`,
                                       { from_store_id: storeId });
      flash(`Applied to ${data.applied_count} other store${data.applied_count === 1 ? "" : "s"}.`);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const policyByMethod = useMemo(() => Object.fromEntries(
    (policies || []).map((p) => [p.method, p])
  ), [policies]);

  const myUserId = user?.user_id;
  const isBlocked = (mid) => {
    const p = policyByMethod[mid];
    if (!p) return null;
    if (!p.master_enabled) return "Disabled by admin";
    if ((p.seller_deny || []).includes(myUserId)) return "Blocked for your account";
    return null;
  };

  if (!stores?.length) {
    return <div className="card p-6 text-center text-[#A1A1AA]">Create a store first to configure payments.</div>;
  }
  if (!config) {
    return <div className="card p-6 flex items-center justify-center gap-2 text-[#A1A1AA]">
      <Loader2 size={16} className="animate-spin"/> Loading payment settings…
    </div>;
  }

  return (
    <div className="space-y-5" data-testid="seller-payments-tab">
      {/* Header / store picker */}
      <div className="card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1">
            Configure payments for
          </div>
          <select className="input-dark" value={storeId}
            data-testid="payments-store-picker"
            onChange={(e) => setStoreId(e.target.value)}>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="btn-primary"
            data-testid="payments-save-btn">
            <Save size={14} className="inline mr-1.5"/> Save
          </button>
          {stores.length > 1 && (
            <button onClick={applyAll} disabled={busy} className="btn-secondary"
              data-testid="payments-apply-all-btn">
              <Copy size={14} className="inline mr-1.5"/> Apply to all stores
            </button>
          )}
        </div>
      </div>

      {msg && <div className="rounded-xl bg-[#D4FF00]/10 border border-[#D4FF00]/30 px-4 py-2 text-sm text-[#D4FF00] flex items-center gap-2" data-testid="payments-toast">
        <CheckCircle2 size={14}/> {msg}
      </div>}
      {err && <div className="rounded-xl bg-[#FF007F]/10 border border-[#FF007F]/30 px-4 py-2 text-sm text-[#FF007F] flex items-center gap-2">
        <AlertCircle size={14}/> {err}
      </div>}

      {/* Method cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {catalog.map((spec) => {
          const Icon = ICONS[spec.icon] || CreditCard;
          const m = config.methods[spec.id] || {};
          const blocked = isBlocked(spec.id);
          return (
            <div key={spec.id} className="card p-4 sm:p-5"
              data-testid={`payments-card-${spec.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#3D2845] flex items-center justify-center">
                    <Icon size={18} className="text-[#D4FF00]"/>
                  </div>
                  <div>
                    <div className="font-display font-bold text-base">{spec.label}</div>
                    {blocked && (
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF007F] flex items-center gap-1 mt-0.5">
                        <ShieldX size={10}/> {blocked}
                      </div>
                    )}
                  </div>
                </div>
                <Toggle checked={!!m.enabled} disabled={!!blocked}
                  testid={`payments-toggle-${spec.id}`}
                  onChange={(v) => setMethod(spec.id, { enabled: v })}/>
              </div>

              {/* Method-specific fields, only shown when enabled */}
              {m.enabled && (
                <div className="grid gap-2 mt-3 pt-3 border-t border-[#3D2845]">
                  {spec.id === "cod" && (
                    <Field label="Note for buyer (optional)"
                      value={m.note || ""}
                      onChange={(v) => setMethod("cod", { note: v })}
                      testid="cod-note"
                      placeholder="Pay rider in cash on delivery"/>
                  )}
                  {spec.id === "qr" && (
                    <>
                      <Field label="QR image URL" required
                        value={m.qr_image_url || ""}
                        onChange={(v) => setMethod("qr", { qr_image_url: v })}
                        testid="qr-image-url"
                        placeholder="https://… (UPI / PromptPay / PayNow)"/>
                      {m.qr_image_url && (
                        <img src={m.qr_image_url} alt="QR preview"
                          className="w-32 h-32 object-contain rounded-lg bg-white p-2 mt-1"/>
                      )}
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Field label="Label" value={m.label || ""}
                          onChange={(v) => setMethod("qr", { label: v })}
                          testid="qr-label" placeholder="Pay via UPI"/>
                        <Field label="Handle / VPA" value={m.handle || ""}
                          onChange={(v) => setMethod("qr", { handle: v })}
                          testid="qr-handle" placeholder="store@upi"/>
                      </div>
                      <Field label="Note (optional)" value={m.note || ""}
                        onChange={(v) => setMethod("qr", { note: v })}
                        testid="qr-note"/>
                    </>
                  )}
                  {spec.id === "bank" && (
                    <>
                      <Field label="Account name" required
                        value={m.account_name || ""}
                        onChange={(v) => setMethod("bank", { account_name: v })}
                        testid="bank-name"/>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Field label="Account number" required
                          value={m.account_number || ""}
                          onChange={(v) => setMethod("bank", { account_number: v })}
                          testid="bank-account"/>
                        <Field label="IFSC / SWIFT"
                          value={m.ifsc_swift || ""}
                          onChange={(v) => setMethod("bank", { ifsc_swift: v })}
                          testid="bank-ifsc"/>
                      </div>
                      <Field label="Note (optional)" value={m.note || ""}
                        onChange={(v) => setMethod("bank", { note: v })}
                        testid="bank-note"
                        placeholder="Add order id in remarks"/>
                    </>
                  )}
                  {spec.id === "gateway" && (
                    <>
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA]">
                          Provider
                        </label>
                        <select className="input-dark mt-1"
                          data-testid="gateway-provider"
                          value={m.provider || "stripe"}
                          onChange={(e) => setMethod("gateway", { provider: e.target.value })}>
                          <option value="stripe">Stripe</option>
                          <option value="razorpay">Razorpay</option>
                          <option value="paypal">PayPal</option>
                        </select>
                      </div>
                      <Field label="Note for buyer (optional)"
                        value={m.note || ""}
                        onChange={(v) => setMethod("gateway", { note: v })}
                        testid="gateway-note"/>
                      <p className="text-[11px] text-[#A1A1AA]">
                        Online gateway is currently <span className="font-bold text-[#FF007F]">MOCKED</span> in this MVP — orders are auto-marked paid for demo. Real Stripe / Razorpay integration is on the roadmap.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Contact card — surfaced to buyer on order detail */}
      <div className="card p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="font-display font-bold text-base">Contact details shared with buyer</div>
        </div>
        <p className="text-[11px] text-[#A1A1AA] mb-3">
          These are shown on the buyer's order page once the order is placed, plus included in the order-placed notification. Defaults to your profile if left empty.
        </p>
        <div className="grid sm:grid-cols-3 gap-2">
          <Field label="Email" icon={Mail}
            value={config.contact?.email || ""}
            onChange={(v) => setContact({ email: v })}
            testid="contact-email"/>
          <Field label="Phone" icon={Phone}
            value={config.contact?.phone || ""}
            onChange={(v) => setContact({ phone: v })}
            testid="contact-phone"/>
          <Field label="Pickup address" icon={MapPin}
            value={config.contact?.pickup_address || ""}
            onChange={(v) => setContact({ pickup_address: v })}
            testid="contact-address"/>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!checked)}
      data-testid={testid}
      aria-pressed={checked}
      className={`relative w-11 h-6 rounded-full transition shrink-0 ${
        disabled ? "bg-[#3D2845] opacity-50 cursor-not-allowed"
                 : checked ? "bg-[#D4FF00]" : "bg-[#3D2845] hover:bg-[#3F3F46]"
      }`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
        checked ? "translate-x-5" : ""
      }`}/>
    </button>
  );
}

function Field({ label, value, onChange, placeholder, required, testid, icon: Icon }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1 flex items-center gap-1">
        {Icon && <Icon size={10}/>} {label}{required && <span className="text-[#FF007F]">*</span>}
      </div>
      <input type="text" className="input-dark w-full"
        value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder}/>
    </label>
  );
}
